/**
 * The surface: our `MessageProcessor`, and what `boardStore` became.
 *
 * The shape is the spec's — a flat `components` map plus a `children` tree —
 * which is markedly simpler than the eight-branch op reducer it replaces:
 * `add`/`update` are one upsert, `remove`/`clear` are a rewritten `children`,
 * `status` is a prop, `focus` is a prop, and `replaces` does not exist because a
 * card and its region arrive in the SAME message.
 *
 * Three things survive from the old store because they were never about the
 * wire format, and losing them would be a visible regression:
 *
 *  * **`lastGood` / `stale`.** Props that fail their schema keep the last
 *    validated ones and the card is marked; a card may not take down the board.
 *  * **enter / flash / exit phases.** The animations are CSS, and the store is
 *    what tells them apart.
 *  * **the unseen counter**, which the mobile tab badge is built on.
 *
 * ── THE EXIT ANIMATION IS THE ONE PLACE THIS IS NOT «JUST THE DIFF» ─────────
 * Under A2UI a removed card is not deleted: it stays in `components` as an
 * orphan and merely leaves its region's `children`. So a diff alone would make
 * it vanish instantly — the collapse the visitor reads as «убрал» needs the node
 * to be drawn for another 180 ms, in the place it just left. Hence `exiting`:
 * per container, the ids that dropped out of `children` together with the index
 * they held, cleared by a timer. `Board.tsx` renders `children ∪ exiting`, and
 * that union is the whole departure from «Board.tsx just reads children».
 * (Agreed with the lead, 2026-09-01, as a legitimate deviation from §5.2.)
 */

import { create } from 'zustand';
import { validateProps, isKnownComponent } from '../catalog/registry';
import { applyPatch } from '../store/jsonPatch';
import { exitDuration } from '../lib/reducedMotion';
import {
  isCreateSurface,
  isDeleteSurface,
  isUpdateComponents,
  isUpdateDataModel,
  messageKind,
  type A2uiComponent,
  type AgentToRenderer,
  type ChildList,
} from './types';
import type { MessageProcessor } from './MessageProcessor';

export const ROOT_ID = 'root';
export const REGION_IDS = {
  header: 'region:header',
  main: 'region:main',
  side: 'region:side',
  footer: 'region:footer',
} as const;

export type Region = keyof typeof REGION_IDS;
/** Client-side only: the wire has no `status` field any more. */
export type NodeStatus = 'ready' | 'stale' | 'error';
export type NodePhase = 'enter' | 'idle' | 'exit';

export type SurfaceNode = {
  id: string;
  component: string;
  /** The whole validated node — properties are flat beside `id`/`component`. */
  props: unknown;
  status: NodeStatus;
  phase: NodePhase;
  revision: number;
  createdAt: number;
  updatedAt: number;
  lastGood?: unknown;
};

/** An id that has left its container's children and is still animating out. */
type Exiting = { id: string; index: number };

export type SurfaceState = {
  surfaceId: string | null;
  catalogId: string | null;
  components: Record<string, SurfaceNode>;
  dataModel: Record<string, unknown>;
  /** From the transport envelope, not from the payload. */
  rev: number;
  unseen: number;
  issues: { at: number; text: string }[];
  exiting: Record<string, Exiting[]>;

  apply: (messages: AgentToRenderer[]) => void;
  /** `GET /state` gave a stored surface and we wrapped it — §2.1. */
  applyCreateSurface: (message: AgentToRenderer, rev: number) => void;
  setRev: (rev: number) => void;
  childrenOf: (id: string) => string[];
  /** `children ∪ exiting`, in the order the region last had them. */
  renderOrder: (containerId: string) => string[];
  regionNodes: (region: Region) => SurfaceNode[];
  focusedId: () => string | null;
  markSeen: () => void;
  reset: () => void;
};

const EMPTY_SURFACE = () => ({
  surfaceId: null as string | null,
  catalogId: null as string | null,
  components: {} as Record<string, SurfaceNode>,
  dataModel: {} as Record<string, unknown>,
  rev: 0,
  unseen: 0,
  issues: [] as { at: number; text: string }[],
  exiting: {} as Record<string, Exiting[]>,
});

const exitTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearExitTimers(): void {
  for (const timer of exitTimers.values()) clearTimeout(timer);
  exitTimers.clear();
}

/** A static child list; a data-model template resolves to nothing to draw yet. */
function staticChildren(children: ChildList | undefined, note: (text: string) => void): string[] {
  if (!children) return [];
  if (Array.isArray(children)) return children.filter((id) => typeof id === 'string');
  // The template form is legal protocol and we never emit it. Drawing nothing is
  // honest; guessing at a data-model list would be a second renderer.
  note(`children template (${children.componentId} over ${children.path}) is not rendered`);
  return [];
}

export const useSurface = create<SurfaceState>((set, get) => {
  function note(text: string): void {
    // eslint-disable-next-line no-console
    console.warn('[a2ui]', text);
    set((state) => ({ issues: [...state.issues.slice(-19), { at: Date.now(), text }] }));
  }

  /** A card that came back before its exit animation finished keeps its slot. */
  function cancelExit(containerId: string, id: string): void {
    const key = `${containerId}\u0000${id}`;
    const timer = exitTimers.get(key);
    if (timer) clearTimeout(timer);
    exitTimers.delete(key);
  }

  function scheduleExit(containerId: string, id: string): void {
    const key = `${containerId}\u0000${id}`;
    const existing = exitTimers.get(key);
    if (existing) clearTimeout(existing);
    exitTimers.set(
      key,
      setTimeout(() => {
        exitTimers.delete(key);
        set((state) => {
          const list = (state.exiting[containerId] ?? []).filter((each) => each.id !== id);
          const exiting = { ...state.exiting };
          if (list.length === 0) delete exiting[containerId];
          else exiting[containerId] = list;
          return { exiting };
        });
      }, exitDuration()),
    );
  }

  /**
   * Upserts a batch of nodes.
   *
   * One message is one transaction: every node lands together, which is what
   * makes the skeleton→card swap atomic without a `replaces` field — the card
   * and the region's new `children` are in the same list.
   */
  function upsert(nodes: A2uiComponent[]): void {
    const state = get();
    const components = { ...state.components };
    const exiting = { ...state.exiting };
    const now = Date.now();
    let unseen = state.unseen;

    for (const raw of nodes) {
      if (!raw || typeof raw.id !== 'string' || typeof raw.component !== 'string') {
        note(`a node with no id/component was skipped: ${JSON.stringify(raw).slice(0, 120)}`);
        continue;
      }
      const previous = components[raw.id];

      // Containers: what left `children` is what animates out.
      if (previous && Array.isArray((previous.props as A2uiComponent)?.children)) {
        const before = staticChildren((previous.props as A2uiComponent).children, note);
        const after = staticChildren(raw.children, note);
        const removed = before
          .map((id, index) => ({ id, index }))
          .filter((each) => !after.includes(each.id));
        // **A card can come BACK while it is still animating out**, and live
        // that is not rare: a full sync rewrites a region's children, and an id
        // dropped one frame ago is in the next one. Its exit has to be
        // cancelled, or `renderOrder` would list it twice — React warns about
        // the duplicate key and is free to drop one of the two.
        const returning = (exiting[raw.id] ?? []).filter((each) => after.includes(each.id));
        for (const back of returning) {
          cancelExit(raw.id, back.id);
          const node = components[back.id];
          if (node) components[back.id] = { ...node, phase: 'idle' };
        }
        let leaving = (exiting[raw.id] ?? []).filter((each) => !after.includes(each.id));

        if (removed.length > 0) {
          leaving = leaving.filter((each) => !removed.some((gone) => gone.id === each.id));
          for (const gone of removed) {
            const node = components[gone.id];
            if (node) components[gone.id] = { ...node, phase: 'exit', updatedAt: now };
            scheduleExit(raw.id, gone.id);
          }
          leaving = [...leaving, ...removed];
        }
        if (leaving.length > 0) exiting[raw.id] = leaving;
        else delete exiting[raw.id];
      }

      if (!isKnownComponent(raw.component)) {
        // Not an error: an older frontend meeting a newer catalogue draws
        // `UnknownCard`, and the text answer is self-sufficient by contract.
        note(`unknown component "${raw.component}" (${raw.id})`);
      }
      const result = validateProps(raw.component, raw);
      let props: unknown = raw;
      let status: NodeStatus = 'ready';
      if (result.ok) {
        props = result.value;
      } else {
        note(`${raw.id} (${raw.component}) failed validation: ${result.error}`);
        // Keep the last good props if there are any; otherwise draw the raw node
        // and say so. A card that renders wrong beats a card that vanishes.
        if (previous?.lastGood !== undefined) {
          props = previous.lastGood;
          status = 'stale';
        } else {
          status = 'error';
        }
      }

      components[raw.id] = {
        id: raw.id,
        component: raw.component,
        props,
        status,
        // An element already on screen must not replay its entrance because a
        // full sync confirmed it.
        phase: previous ? 'idle' : 'enter',
        revision: (previous?.revision ?? 0) + 1,
        createdAt: previous?.createdAt ?? now,
        updatedAt: now,
        lastGood: status === 'ready' ? props : previous?.lastGood,
      };
      unseen += 1;
    }

    set({ components, exiting, unseen });
  }

  function setDataModel(path: string | undefined, value: unknown): void {
    const state = get();
    if (!path || path === '/') {
      set({ dataModel: (value ?? {}) as Record<string, unknown> });
      return;
    }
    try {
      // `value: null` deletes at `path` — the spec says so in as many words.
      const patch = value === null ? { op: 'remove' as const, path } : { op: 'add' as const, path, value };
      const next = applyPatch(state.dataModel, [patch]);
      set({ dataModel: (next ?? {}) as Record<string, unknown> });
    } catch (error) {
      note(`updateDataModel ${path} did not apply (${String(error)})`);
    }
  }

  return {
    ...EMPTY_SURFACE(),

    apply: (messages) => {
      for (const message of messages) {
        if (isUpdateComponents(message)) {
          const body = message.updateComponents;
          if (body.surfaceId && get().surfaceId && body.surfaceId !== get().surfaceId) {
            note(`updateComponents for another surface (${body.surfaceId}) was ignored`);
            continue;
          }
          upsert(body.components ?? []);
        } else if (isUpdateDataModel(message)) {
          setDataModel(message.updateDataModel.path, message.updateDataModel.value);
        } else if (isCreateSurface(message)) {
          const body = message.createSurface;
          // A surfaceId may not be re-created; a repeat is the reconnect path
          // and we treat it as a full re-instantiation, which is what it is.
          // The pending exits go with the surface they belonged to: a timer
          // outliving it fires against the same (region, card) pair of the NEW
          // board and takes a card off the screen mid-animation.
          clearExitTimers();
          set({
            ...EMPTY_SURFACE(),
            surfaceId: body.surfaceId,
            catalogId: body.catalogId ?? null,
            dataModel: body.dataModel ?? {},
          });
          upsert(body.components ?? []);
          set({ unseen: 0 });
        } else if (isDeleteSurface(message)) {
          // We never send one; honouring it costs nothing and refusing a legal
          // message would be wrong.
          get().reset();
        } else {
          // `callRendererFunction` / `agentFunctionResponse`: part of the
          // protocol, not part of our binding. Ignored out loud, never thrown —
          // the same discipline `UnknownCard` follows.
          note(`message "${messageKind(message)}" is not implemented by this renderer`);
        }
      }
    },

    applyCreateSurface: (message, rev) => {
      const state = get();
      // The race this exists for: `GET /state` answers with rev R while live
      // frames with a higher rev have already been applied. The older document
      // must not overwrite them.
      if (rev < state.rev && Object.keys(state.components).length > 0) {
        note(`createSurface rev=${rev} is older than the live rev=${state.rev}; ignored`);
        return;
      }
      get().apply([message]);
      set({ rev: Math.max(rev, state.rev) });
    },

    setRev: (rev) => set((state) => ({ rev: Math.max(rev, state.rev) })),

    childrenOf: (id) => {
      const node = get().components[id];
      if (!node) return [];
      return staticChildren((node.props as A2uiComponent)?.children, note);
    },

    renderOrder: (containerId) => {
      const state = get();
      const live = state.childrenOf(containerId);
      const leaving = state.exiting[containerId] ?? [];
      if (leaving.length === 0) return live;
      // Each departing id goes back where it stood, so the collapse happens in
      // place instead of at the end of the list.
      const out = [...live];
      for (const each of [...leaving].sort((a, b) => a.index - b.index)) {
        // Belt under the bookkeeping above: an id that is live is never also
        // drawn as leaving, whatever `exiting` happens to hold.
        if (!state.components[each.id] || out.includes(each.id)) continue;
        out.splice(Math.min(each.index, out.length), 0, each.id);
      }
      return out;
    },

    regionNodes: (region) => {
      const state = get();
      return state
        .renderOrder(REGION_IDS[region])
        .map((id) => state.components[id])
        .filter((node): node is SurfaceNode => node !== undefined);
    },

    focusedId: () => {
      const root = get().components[ROOT_ID];
      const value = (root?.props as { focusedId?: unknown })?.focusedId;
      return typeof value === 'string' ? value : null;
    },

    markSeen: () => set({ unseen: 0 }),

    reset: () => {
      clearExitTimers();
      set(EMPTY_SURFACE());
    },
  };
});

/** The store IS the processor — this is the binding, named for what it is. */
export const surfaceProcessor: MessageProcessor = {
  apply: (messages) => useSurface.getState().apply(messages),
};
